"use client";

import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { useAuthStore } from "@/stores/authStore";

export type SupplierRow = {
  id: number;
  name: string;
  type?: string | null;
  phone?: string | null;
  email?: string | null;
  gstin?: string | null;
  pan?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  credit_limit?: number;
  outstanding_balance?: number;
  payment_terms?: number;
  bank_name?: string | null;
  account_no?: string | null;
  ifsc?: string | null;
  is_active?: number;
};

export function useSuppliers(search?: string) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["suppliers", search],
    queryFn: async () => {
      const { data } = await api.get<{ suppliers: SupplierRow[] }>("/api/suppliers", {
        params: search ? { search } : undefined,
      });

      return data.suppliers;
    },
    enabled: Boolean(token),
  });
}

export function useSupplier(id: number | null) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["supplier", id],
    queryFn: async () => {
      const { data } = await api.get<{ supplier: SupplierRow }>(`/api/suppliers/${id}`);

      return data.supplier;
    },
    enabled: Boolean(token) && id != null && id > 0,
  });
}

export function useSupplierLedger(id: number | null) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["supplier-ledger", id],
    queryFn: async () => {
      const { data } = await api.get<{ ledger: unknown[] }>(`/api/suppliers/${id}/ledger`);

      return data.ledger;
    },
    enabled: Boolean(token) && id != null && id > 0,
  });
}

export type SupplierCreateValues = {
  name: string;
  phone?: string | null;
  gstin?: string | null;
  email?: string | null;
  address?: string | null;
  is_active?: number;
};

export function useCreateSupplier() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (values: SupplierCreateValues) => {
      const payload: Record<string, unknown> = {
        name: values.name.trim(),
        phone: values.phone?.trim() ? values.phone.trim() : null,
        gstin: values.gstin?.trim() ? values.gstin.trim() : null,
        email: values.email?.trim() ? values.email.trim() : null,
        address: values.address?.trim() ? values.address.trim() : null,
        is_active: values.is_active ?? 1,
      };
      const { data } = await api.post<{ supplier: SupplierRow }>("/api/suppliers", payload);
      return data.supplier;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
    retry: false,
    meta: { requiresAuth: true },
    // guard: prevent fire before login
    ...(token ? {} : { mutationFn: async () => Promise.reject(new Error("Not authenticated")) }),
  });
}
